import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Trash2, Loader2, Reply, ChevronDown, ChevronUp, Pencil, Check, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function StoryComments({ storyId, isOpen, onClose, currentUser, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [expandedReplies, setExpandedReplies] = useState(new Set());
  const inputRef = useRef(null);
  const editInputRef = useRef(null);
  const commentsEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && storyId) fetchComments();
  }, [isOpen, storyId]);

  useEffect(() => {
    if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  const fetchComments = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/stories/${storyId}/comments`);
      setComments(res.data || []);
      onCountChange?.(res.data?.length || 0);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;
    const commentText = newComment.trim();
    const parentId = replyingTo;
    setNewComment('');
    setReplyingTo(null);
    setIsSubmitting(true);

    const optimistic = {
      id: `temp-${Date.now()}`,
      story_id: storyId,
      user_id: currentUser?.user_id,
      user_name: currentUser?.name || 'You',
      user_picture: currentUser?.picture,
      content: commentText,
      parent_id: parentId || null,
      created_at: new Date().toISOString(),
      _isPending: true
    };
    setComments(prev => [...prev, optimistic]);
    onCountChange?.(comments.length + 1);
    if (parentId) setExpandedReplies(prev => new Set([...prev, parentId]));

    try {
      const payload = { content: commentText };
      if (parentId) payload.parent_id = parentId;
      const res = await axios.post(`${API}/stories/${storyId}/comments`, payload);
      setComments(prev => prev.map(c => c.id === optimistic.id ? { ...res.data, _isNew: true } : c));
    } catch (err) {
      console.error('Failed to add comment:', err);
      setComments(prev => prev.filter(c => c.id !== optimistic.id));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    const deleted = comments.find(c => c.id === commentId);
    setComments(prev => prev.filter(c => c.id !== commentId && c.parent_id !== commentId));
    onCountChange?.(Math.max(0, comments.length - 1 - comments.filter(c => c.parent_id === commentId).length));
    try {
      await axios.delete(`${API}/stories/${storyId}/comments/${commentId}`);
    } catch (err) {
      if (deleted) setComments(prev => [...prev, deleted].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
    }
  };

  const handleEdit = async (commentId) => {
    if (!editContent.trim()) return;
    const oldComment = comments.find(c => c.id === commentId);
    
    // Optimistic update
    setComments(prev => prev.map(c => c.id === commentId ? { ...c, content: editContent.trim(), edited_at: new Date().toISOString() } : c));
    setEditingId(null);
    setEditContent('');
    
    try {
      await axios.put(`${API}/stories/${storyId}/comments/${commentId}`, { content: editContent.trim() });
    } catch (err) {
      console.error('Failed to edit comment:', err);
      // Revert on error
      if (oldComment) setComments(prev => prev.map(c => c.id === commentId ? oldComment : c));
    }
  };

  const startEdit = (comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const formatTime = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(diff / 3600000);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(diff / 86400000);
    if (days < 7) return `${days}d`;
    return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  const topLevel = comments.filter(c => !c.parent_id);
  const getReplies = (parentId) => comments.filter(c => c.parent_id === parentId);

  const CommentItem = ({ comment, isReply = false }) => {
    const replies = getReplies(comment.id);
    const isExpanded = expandedReplies.has(comment.id);
    const isMine = comment.user_id === currentUser?.user_id;
    const isEditing = editingId === comment.id;

    return (
      <div className={isReply ? 'ml-8 mt-2' : ''}>
        <motion.div
          initial={comment._isNew || comment._isPending ? { opacity: 0, y: 5 } : false}
          animate={{ opacity: comment._isPending ? 0.6 : 1, y: 0 }}
          className="group"
          data-testid={`comment-${comment.id}`}
        >
          {/* Name row */}
          <div className="flex items-center space-x-2 mb-1">
            {comment.user_picture ? (
              <img src={comment.user_picture} alt="" className="w-6 h-6 rounded-full object-cover" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-semibold text-muted-foreground">{comment.user_name?.charAt(0)?.toUpperCase() || '?'}</span>
              </div>
            )}
            <span className="text-sm font-semibold text-foreground">{comment.user_name}</span>
            <span className="text-[10px] text-muted-foreground/60">{formatTime(comment.created_at)}</span>
            {comment.edited_at && <span className="text-[10px] text-muted-foreground/40">(edited)</span>}
          </div>

          {/* Comment text or edit input */}
          {isEditing ? (
            <div className="pl-8 flex items-center gap-2">
              <input
                ref={editInputRef}
                type="text"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleEdit(comment.id);
                  if (e.key === 'Escape') cancelEdit();
                }}
                className="flex-1 bg-secondary/50 rounded px-2 py-1 text-sm text-foreground border border-border focus:outline-none focus:border-primary"
                data-testid={`edit-input-${comment.id}`}
              />
              <button onClick={() => handleEdit(comment.id)} className="p-1 text-primary hover:bg-primary/10 rounded" data-testid={`save-edit-${comment.id}`}>
                <Check className="w-4 h-4" />
              </button>
              <button onClick={cancelEdit} className="p-1 text-muted-foreground hover:bg-secondary rounded" data-testid={`cancel-edit-${comment.id}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <p className="text-sm text-foreground/85 leading-relaxed pl-8 break-words">{comment.content}</p>
          )}

          {/* Actions row */}
          {!isEditing && (
            <div className="flex items-center space-x-3 pl-8 mt-1">
              {!isReply && (
                <button
                  onClick={() => { setReplyingTo(comment.id); inputRef.current?.focus(); }}
                  className="text-[11px] text-muted-foreground/70 hover:text-primary transition-colors flex items-center space-x-1"
                  data-testid={`reply-button-${comment.id}`}
                >
                  <Reply className="w-3 h-3" /><span>Reply</span>
                </button>
              )}
              {isMine && !comment._isPending && (
                <>
                  <button
                    onClick={() => startEdit(comment)}
                    className="text-[11px] text-muted-foreground/40 hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`edit-comment-${comment.id}`}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleDelete(comment.id)}
                    className="text-[11px] text-muted-foreground/40 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`delete-comment-${comment.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
              {comment._isPending && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground ml-1" />}
            </div>
          )}
        </motion.div>

        {/* Replies */}
        {replies.length > 0 && !isReply && (
          <div className="mt-1">
            {replies.length > 1 && !isExpanded ? (
              <button
                onClick={() => setExpandedReplies(prev => new Set([...prev, comment.id]))}
                className="ml-8 text-[11px] text-primary/70 hover:text-primary flex items-center space-x-1 py-1"
                data-testid={`expand-replies-${comment.id}`}
              >
                <ChevronDown className="w-3 h-3" /><span>{replies.length} replies</span>
              </button>
            ) : null}
            {(isExpanded || replies.length === 1) && (
              <>
                {replies.map(r => <CommentItem key={r.id} comment={r} isReply />)}
                {replies.length > 1 && (
                  <button
                    onClick={() => setExpandedReplies(prev => { const n = new Set(prev); n.delete(comment.id); return n; })}
                    className="ml-8 text-[11px] text-muted-foreground/60 hover:text-primary flex items-center space-x-1 py-1"
                  >
                    <ChevronUp className="w-3 h-3" /><span>Hide replies</span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="mt-3 pt-3 border-t border-border/50"
        data-testid="story-comments"
      >
        <div className="space-y-3 mb-3 max-h-64 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : topLevel.length === 0 ? (
            <p className="text-xs text-muted-foreground/60 text-center py-2">No comments yet</p>
          ) : (
            topLevel.map(c => <CommentItem key={c.id} comment={c} />)
          )}
          <div ref={commentsEndRef} />
        </div>

        {/* Reply indicator */}
        {replyingTo && (
          <div className="flex items-center justify-between bg-primary/5 rounded-lg px-3 py-1.5 mb-2 text-xs text-primary">
            <span>Replying to {comments.find(c => c.id === replyingTo)?.user_name || '...'}</span>
            <button onClick={() => setReplyingTo(null)} className="text-muted-foreground hover:text-foreground" data-testid="cancel-reply">&times;</button>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="flex items-center space-x-2">
          <input
            ref={inputRef}
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={replyingTo ? "Write a reply..." : "Add a comment..."}
            className="flex-1 bg-transparent border-none text-sm focus:outline-none text-foreground placeholder:text-muted-foreground/50"
            data-testid="comment-input"
            disabled={isSubmitting}
          />
          <button
            type="submit"
            disabled={!newComment.trim() || isSubmitting}
            className="p-1.5 rounded-full text-primary hover:bg-primary/10 transition-colors disabled:opacity-30"
            data-testid="submit-comment"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </motion.div>
    </AnimatePresence>
  );
}

export default StoryComments;
